import React, { useState } from 'react';
import { useChangeLanguageManager } from '@hero/web-hooks';
import type { LanguageInfo } from '@hero/web-hooks';
import { CheckIcon, ChevronDownIcon, GlobeIcon } from '@hero/web-icons';
import type { Language } from '@hero/schema';
import { cn } from '@hero/web-lib';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../../ui/DropdownMenu/DropdownMenu';

function LanguageItems({
  languages,
  currentCode,
  isChangingLanguage,
  onSelect,
}: {
  languages: LanguageInfo[];
  currentCode: Language;
  isChangingLanguage: boolean;
  onSelect: (code: Language) => void;
}) {
  return (
    <>
      {languages.map(lang => {
        const isActive = currentCode === lang.code;
        return (
          <DropdownMenuItem
            key={lang.code}
            onSelect={() => onSelect(lang.code)}
            disabled={isChangingLanguage}
            className="cursor-pointer">
            <div className="flex items-center gap-2 w-full">
              <span className="text-lg leading-none">{lang.emoji}</span>
              <span
                className={cn('text-sm', isActive && 'font-semibold text-primary')}>
                {lang.nativeName}
              </span>
              {isActive && <CheckIcon className="ms-auto size-4 text-primary" />}
            </div>
          </DropdownMenuItem>
        );
      })}
    </>
  );
}

interface LanguageSelectorProps {
  className?: string;
}

/**
 * Standalone language dropdown — used on auth pages (HeaderAuth).
 * For embedding inside another menu, use `<LanguageSelectorSubmenu />`.
 */
const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className }) => {
  const { changeLanguage, isChangingLanguage, current, languages } = useChangeLanguageManager();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex items-center gap-2 px-4 py-2 bg-white border rounded-lg border-border focus:outline-none',
          className,
        )}
        disabled={isChangingLanguage}>
        <span className="text-lg leading-none">{current.emoji}</span>
        <span className="text-sm">{current.shortName}</span>
        <ChevronDownIcon className="size-4 text-gray-600" />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <LanguageItems
          languages={languages}
          currentCode={current.code}
          isChangingLanguage={isChangingLanguage}
          onSelect={changeLanguage}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface LanguageSelectorSubmenuProps {
  label?: string;
}

/**
 * Inline language picker — designed to be placed inside an existing
 * `<DropdownMenuContent>` (e.g. UserProfileMenu).
 *
 * Implementation note: this used to be a `<DropdownMenuSub>` (Radix
 * sub-menu) but Radix's "safe-triangle" pointer tracking, combined
 * with a manual click toggle that bypassed open-on-hover, made the
 * sub-menu close as the user moved the cursor toward it — the
 * cursor would briefly leave the trigger box before reaching the
 * sub-menu content, and Radix would fire `onOpenChange(false)` mid-
 * traversal. The fix is to drop the sub-menu entirely and render
 * the language options inline as `DropdownMenuItem`s in the parent
 * menu. With only ~2-3 languages this is the better UX anyway: one
 * click selects a language directly instead of two clicks (open
 * sub-menu, then pick).
 *
 * The component name is kept (`LanguageSelectorSubmenu`) for
 * backwards-compatibility with existing call-sites; it now renders
 * a label row + the language items, not a Radix sub-menu.
 */
export const LanguageSelectorSubmenu: React.FC<LanguageSelectorSubmenuProps> = ({ label }) => {
  const { changeLanguage, isChangingLanguage, current, languages } = useChangeLanguageManager();

  return (
    <>
      <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <GlobeIcon className="size-4" />
        <span>{label ?? current.nativeName}</span>
      </DropdownMenuLabel>
      <LanguageItems
        languages={languages}
        currentCode={current.code}
        isChangingLanguage={isChangingLanguage}
        onSelect={changeLanguage}
      />
    </>
  );
};

export default LanguageSelector;
